/**
 * `auth.ts` — taksonomi izin: **scope M2M** & **permission POS**.
 *
 * Dua sumbu berbeda, dan menyamakannya adalah kesalahan yang mahal:
 *
 * | | Siapa | Contoh | Dari |
 * |---|---|---|---|
 * | **scope M2M** | mesin (konsol Nexus, companion, MCP) | `platform:provision` · `reports:read` | token OAuth2 |
 * | **permission POS** | manusia (kasir, penyelia) | `order:void` · `shift:close` | `pos_role_permission` (§4b②) |
 *
 * ⚠️ §4b② sudah menegaskannya: *"Jabatan dari HR — IZIN POS milik POS"*. Mesin tak punya jabatan,
 * manusia tak punya scope OAuth. Satu daftar untuk keduanya akan memaksa salah satunya dipetakan
 * paksa, dan pemetaan paksa itulah yang membuat izin bocor.
 *
 * @module
 */
export const M2M_SCOPES = Object.freeze([
    /** Membuat & memigrasi tenant. Dipakai konsol platform OSB Nexus. */
    'platform:provision',
    /** Membaca status tenant/outlet. */
    'platform:read',
    /** Membaca katalog & master outlet. */
    'master:read',
    /** Mengubah master (menu, harga). ⚠️ Untuk agen AI: ber-persetujuan (`AI-SURFACE.md` R-AI-3). */
    'master:write',
    /** Membaca laporan & ringkasan. */
    'reports:read',
    /** Membaca kesehatan sinkronisasi & antrean (R2). */
    'ops:read',
    /** Membuka sesi kasir dari perangkat terdaftar (dipakai `pos-web` lewat device token). */
    'pos:operate',
]);
/**
 * ⭐⭐⭐ **Scope yang boleh diberikan TENANT kepada kliennya sendiri, dari portal.**
 *
 * ⛔ Bukan seluruh {@link M2M_SCOPES}. Yang tak ada di sini hanya bisa diberikan operator lewat
 * CLI, dan itu disengaja:
 *
 * - `platform:*` — lintas tenant. Kalau tenant bisa memberikannya kepada kliennya sendiri, satu
 *   layar "Integrasi" cukup untuk membaca (dan mem-provision) seluruh tenant lain. Isolasi K1
 *   hanya sekuat pemilih koneksinya, dan pemilih itu percaya scope.
 * - `pos:operate` — jalur KASIR. Ia turun lewat **pemasangan perangkat** (`device_secret`, K21)
 *   supaya tiap token terikat satu mesin terdaftar. Klien M2M ber-`pos:operate` adalah kasir tanpa
 *   mesin: sesi kas, laci, dan tender kehilangan jangkarnya.
 * - `master:write` — R-AI-3 menuntut tulisan master ber-**persetujuan** + `actor_kind='agent'` +
 *   `reason_code`. Mesin persetujuan itu belum ada; memberikan scope-nya sekarang berarti agen
 *   bisa mengubah harga tanpa satu pun langkah yang R-AI-3 syaratkan. ⇒ Dibuka **bersama**
 *   mesinnya, bukan sebelum.
 */
export const TENANT_GRANTABLE_SCOPES = Object.freeze([
    'master:read',
    'reports:read',
    'ops:read',
]);
/** Apakah scope ini boleh diberikan tenant sendiri dari portal? */
export function isTenantGrantableScope(v) {
    return typeof v === 'string' && TENANT_GRANTABLE_SCOPES.includes(v);
}
/**
 * ⭐ **Awalan `client_id` milik tenant** — `t:<tenant>:<nama>`.
 *
 * `m2m_client.client_id` UNIK secara GLOBAL. Tanpa awalan, tenant pertama yang membuat klien
 * bernama `reporting` menguasai nama itu untuk seluruh sistem, dan tenant berikutnya mendapat
 * galat unik yang tak bisa ia jelaskan (ia tak bisa melihat klien tenant lain). Awalannya dipasang
 * **server**, tak pernah diketik pengguna.
 */
export function tenantClientId(tenantCode, name) {
    return `t:${tenantCode}:${name}`;
}
const SCOPE_SET = new Set(M2M_SCOPES);
/** Guard {@link M2mScope}. */
export function isM2mScope(v) {
    return typeof v === 'string' && SCOPE_SET.has(v);
}
/**
 * Permission POS untuk **manusia** — diberikan lewat `pos_role_permission` (§4b②).
 *
 * ⚠️ `AI-SURFACE.md` R-AI-2: agen memperoleh permission yang **sama** dengan manusia, bukan jalur
 * pintas. Kalau void sesudah kirim menuntut persetujuan penyelia, agen pun menuntutnya.
 */
export const POS_PERMISSIONS = Object.freeze([
    'order:create',
    'order:void',
    'order:refund',
    /**
     * ⭐ **Membuka kembali bill yang SUDAH LUNAS** ("release payment" / panggil balik).
     *
     * Dipisahkan dari `order:refund` dengan sengaja: refund mengembalikan uang kepada tamu, ini
     * **membatalkan pencatatan pembayaran yang salah** — kasir salah pilih tender, atau lupa diskon,
     * dan tamunya masih berdiri di depan kasir. Dua peristiwa ekonomi yang berbeda; menyatukan
     * izinnya berarti siapa pun yang boleh refund otomatis boleh membongkar bill lunas.
     */
    'order:reopen',
    /**
     * ⭐⭐ **Merebut kunci sunting pesanan dari orang lain** ("paksa buka meja").
     *
     * Kebutuhannya nyata dan sehari-hari: HP pelayan mati di tengah menyunting, dan mejanya jadi
     * tak bisa disentuh siapa pun. Di VTEC ini dikerjakan lewat kasir (Imron) — jadi izinnya
     * dipisah, bukan dititipkan ke `order:create` yang dimiliki semua pelayan.
     *
     * ⛔ Sengaja BUKAN izin bawaan pelayan. Kalau setiap pelayan bisa merebut bill pelayan lain,
     * kunci ini berhenti menjadi kunci dan hanya menambah satu ketukan. ⚠️ Merebut kunci **milik
     * sendiri** dari perangkat lain tak butuh izin ini — memaksa orang memanggil kasir untuk
     * membuka kuncinya sendiri hanya melatih semua orang memakai paksa-buka sebagai jalan biasa.
     */
    'order:takeover',
    /**
     * ⭐ **Membuka laci kas TANPA transaksi** (tukar uang, setor sebagian, tambah modal).
     *
     * ⛔ Izin tersendiri, dan bukan sekadar formalitas: laci yang terbuka tanpa penjualan adalah
     * jalur pengambilan uang yang **tak meninggalkan jejak fisik apa pun** — tak ada struk, tak ada
     * baris pesanan, tak ada tender. Satu-satunya yang tersisa adalah catatan yang kita buat sendiri.
     */
    'drawer:open',
    /**
     * ⭐⭐⭐ **Menerima pembayaran** (tender + menutup bill).
     *
     * ⛔ Ditambahkan 2026-08-03 bersama peran PELAYAN, dan bukan formalitas: sebelum ini rute
     * `POST /orders/:id/tenders` **sama sekali tak bergerbang izin staf** — siapa pun yang memegang
     * token perangkat bisa mencatat pembayaran, dan `actor`-nya diambil dari **body permintaan**.
     * Artinya "siapa yang menerima uang ini" dijawab oleh yang mengirim permintaannya sendiri.
     *
     * ⚠️ Inilah yang membedakan pelayan dari kasir secara SUNGGUHAN. Pelayan boleh membuka meja,
     * menambah baris, dan mengirim ke dapur; ia **tak** memegang uang. Kalau pembedanya cuma tombol
     * yang disembunyikan di layar, ia bukan kontrol — ia saran.
     */
    'payment:take',
    'order:discount',
    /**
     * ⭐⭐ **Menyetujui promo bertanda `requires_approval`** (diskon karyawan, "diskon manajer",
     * kompensasi tamu komplain).
     *
     * ⛔ Sengaja BUKAN `order:discount`. Kalau penyetujunya cukup ber-`order:discount`, kasir yang
     * menerapkan promo itu sudah memilikinya sendiri — persetujuan pun merosot jadi satu ketukan
     * tambahan atas nama sendiri, dan tandanya di master jadi hiasan.
     *
     * ⚠️ Ditegakkan lewat **step-up PIN** (seperti `drawer:open`), bukan lewat izin sesi yang sedang
     * berjalan: yang dibuktikan adalah **kehadiran** penyelia di depan mesin saat itu, bukan bahwa
     * seseorang pernah login delapan jam lalu.
     */
    'promo:approve',
    'shift:open',
    'shift:close',
    'day:close',
    'master:edit',
    'report:view',
]);
const PERM_SET = new Set(POS_PERMISSIONS);
/** Guard {@link PosPermission}. */
export function isPosPermission(v) {
    return typeof v === 'string' && PERM_SET.has(v);
}
/**
 * ⭐⭐⭐ **Awalan `sub` untuk token PERANGKAT.**
 *
 * `TokenClaims.sub` sudah lama menyatakan bentuk ini di komentarnya, tapi tak pernah ada kode yang
 * membacanya — jadi peran perangkat hidup di `localStorage` klien, bukan di pendaftarannya. K4
 * menyatakan sebaliknya: *"yang menentukan bentuk layar adalah PERANGKATnya, bukan orangnya"* —
 * dan selama peran diambil dari peramban, siapa pun yang membuka DevTools bisa mengubah tablet
 * dapur jadi terminal kasir.
 *
 * ⛔ Awalannya diparsing & DIVALIDASI, bukan diasumsikan. Konvensi yang cuma disepakati di komentar
 * akan suatu hari bertemu `client_id` bernama `device:` yang bukan perangkat, dan tak ada yang
 * menolaknya (Guardrail #2: klasifikasi wajib struktural, bukan konvensi).
 */
export const DEVICE_SUB_PREFIX = 'device:';
/** Bentuk `sub` untuk sebuah perangkat. ⚠️ Satu-satunya tempat awalannya dirakit. */
export function deviceSub(deviceCode) {
    return `${DEVICE_SUB_PREFIX}${deviceCode}`;
}
/**
 * Kode perangkat dari `sub`, atau `undefined` bila token ini bukan token perangkat.
 *
 * ⚠️ Kode kosong (`"device:"`) ditolak — ia lolos pemeriksaan awalan tapi tak menunjuk perangkat
 * mana pun, dan yang menerimanya akan mencari baris yang tak akan pernah ada.
 */
export function deviceCodeFromSub(sub) {
    if (!sub.startsWith(DEVICE_SUB_PREFIX))
        return undefined;
    const code = sub.slice(DEVICE_SUB_PREFIX.length).trim();
    return code === '' ? undefined : code;
}
/**
 * ⭐⭐ **Bentuk layar untuk sebuah peran perangkat** (K4).
 *
 * ⛔ `undefined` = peran itu **belum punya layar**, dan itu jawaban yang sah — bukan alasan untuk
 * jatuh ke kasir. Tablet ringkasan dapur yang diam-diam menampilkan layar bayar jauh lebih buruk
 * daripada pesan "peran ini belum punya layar": yang pertama mengundang orang menerima uang di
 * mesin yang bukan till.
 *
 * ⚠️ `pos_terminal` TETAP memakai shell kasir — ia memang mengambil pesanan. Yang membedakannya
 * dari `cashier` bukan bentuk layarnya, melainkan hak menerima uang, dan itu ditegakkan server
 * saat sesi kas dibuka (K20).
 */
export function shellForDeviceRole(role) {
    switch (role) {
        case 'cashier':
        case 'pos_terminal':
            return 'cashier';
        case 'waiter_device':
            return 'waiter';
        case 'kds_station':
            return 'kds';
        case 'queue_display':
            return 'queue';
        /* ⛔ Belum ada layarnya: kiosk (K7 belum jadi), ringkasan dapur, dan agen cetak yang memang
           bukan layar sama sekali. */
        case 'kiosk':
        case 'kds_summary':
        case 'print_agent':
            return undefined;
    }
}
/** Bentuk layar `pos-web`. ⚠️ Nama di sini = nilai yang dipakai klien, jadi ia ikut kontrak. */
export const POS_SHELLS = Object.freeze(['cashier', 'waiter', 'kds', 'queue']);
/**
 * Umur sesi staf, detik. **Delapan jam** — satu shift penuh.
 *
 * ⚠️ Lebih pendek dari ini berarti kasir diminta memasukkan PIN di tengah antrean, dan kontrol
 * yang mengganggu pekerjaan wajar akan dimatikan orang. Lebih panjang berarti terminal yang
 * ditinggalkan tetap memegang identitas seseorang setelah ia pulang.
 */
export const STAFF_SESSION_TTL_SECONDS = 8 * 60 * 60;
/** Audience token sesi staf — sengaja BEDA dari token mesin, supaya tak bisa saling dipakai. */
export const STAFF_TOKEN_AUDIENCE = 'osbpos-staff-session';
/**
 * ⭐⭐ Audience token & assertion **MESIN** (M2M) — dan kenapa ia pindah ke kontrak 2026-08-06.
 *
 * Nilainya dulu hanya hidup di `portal-api/auth/token.service.ts`. Begitu ada klien di luar repo
 * portal yang harus menandatangani assertion — `apps/mcp-server` yang pertama — nilainya tersalin,
 * dan salinan yang salah **tidak** gagal saat dibangun: ia gagal saat runtime dengan
 * "token ditolak", pesan yang bentuknya sama persis dengan kunci salah atau klien dicabut.
 *
 * ⛔ Audience adalah kontrol: ia yang mencegah assertion untuk sistem lain diputar ulang ke sini.
 * Kontrol yang nilainya digandakan adalah kontrol yang menunggu selisih ejaan.
 */
export const M2M_TOKEN_AUDIENCE = 'osbpos-portal-api';
/* ═══════════════ kebijakan PIN — SATU sumber untuk server & tablet ══════════ */
/**
 * ⭐⭐ Kebijakan penguncian PIN tinggal di KONTRAK, bukan di masing-masing lapis.
 *
 * PIN diperiksa di dua tempat sekarang: server (`pos-core/pin.ts`) dan — sejak tier satu-tablet
 * boleh menyala luring — **tablet itu sendiri**. ⛔ Dua salinan angka kebijakan akan menyimpang,
 * dan yang menyimpang ke arah longgar tak akan ada yang menyadarinya: tablet yang mengunci setelah
 * 20 percobaan tetap "berfungsi", cuma tak lagi melindungi apa pun.
 *
 * ⚠️ PIN 4–8 digit hanya punya 10.000–100 juta kemungkinan. Hash lambat cuma memperlambat;
 * **penguncianlah** pertahanan yang sebenarnya.
 */
export const MAX_PIN_ATTEMPTS = 5;
/** Lama penguncian setelah `MAX_PIN_ATTEMPTS` gagal berturut-turut, milidetik. */
export const PIN_LOCK_MS = 5 * 60 * 1000;
/**
 * ⭐⭐ Panjang minimum kata sandi konsol — **SATU angka untuk seluruh jalur**.
 *
 * Sebelum ini angkanya hidup di lima tempat sekaligus dan SUDAH menyimpang: `bootstrap-owner`
 * menuntut 12, `console-user` menuntut 12, `hashSecret` menuntut 10, dan dua kotak isian di
 * `portal-web` memakai `min: 12` yang ditulis tangan. ⛔ Yang berbahaya bukan angkanya melainkan
 * LAPISANnya: begitu ambang di atas turun di bawah lantai `hashSecret`, sandi yang sudah LOLOS
 * validasi ditolak lagi satu lapis lebih dalam — dengan kode `AUTH-401-PIN` dan pesan yang
 * menyebut angka yang tak pernah ditulis siapa pun. Alasan yang sama dengan `MAX_PIN_ATTEMPTS`
 * di atas: kebijakan yang ditegakkan di banyak tempat wajib punya satu sumber.
 *
 * ⚠️ **8, bukan 12** (Imron, 2026-08-22). Ini memang lebih longgar, dan itu keputusan sadar: 8
 * adalah dasar yang sama dengan NIST SP 800-63B untuk rahasia yang dipilih manusia. Yang
 * sebenarnya menahan tebakan di pintu ini bukan panjangnya melainkan **penguncian**
 * (`MAX_PIN_ATTEMPTS` gagal ⇒ terkunci `PIN_LOCK_MS`) — tanpa itu, 12 karakter pun cuma
 * memperlambat.
 */
export const MIN_PASSWORD_LENGTH = 8;
/**
 * ⭐⭐⭐ Umur maksimum **salinan login luring** — 7 hari sejak login DARING terakhir.
 *
 * ⛔ Angka ini yang membatasi kerusakan dari satu-satunya hal yang tak bisa diperbaiki luring:
 * izin di salinan itu **tak tahu** kalau stafnya sudah dicabut aksesnya di portal. Tablet yang
 * luring tak bisa diberi tahu, jadi yang tersisa cuma umur.
 *
 * ⚠️ Bukan lebih panjang: tablet yang luring lebih dari seminggu punya masalah yang jauh lebih
 * besar daripada login (antrean `outbox` menumpuk, katalognya basi). Bukan lebih pendek: warung
 * yang internetnya putus tiga hari harus tetap bisa berjualan — itu inti janji K13.
 */
export const OFFLINE_LOGIN_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Apakah sesi staf memuat izin yang diminta? */
export function hasPermission(claims, perm) {
    return claims.permissions.includes(perm);
}
/**
 * ⭐ Operasi yang **WAJIB** dilakukan manusia ber-izin — bukan oleh mesin, bukan oleh body yang
 * mengaku-ngaku. Dipakai uji struktural agar tak ada endpoint sensitif yang lupa dijaga.
 */
export const PERMISSION_REQUIRED_OPS = Object.freeze({
    'order:void': 'membatalkan pesanan',
    'order:reopen': 'membuka kembali bill lunas',
    'order:refund': 'mengembalikan uang',
    'order:discount': 'memberi diskon',
    'shift:close': 'menutup sesi kas',
    'day:close': 'menutup hari usaha',
});
/** Apakah klaim memuat scope yang diminta? */
export function hasScope(claims, scope) {
    return claims.scopes.includes(scope);
}
/**
 * Umur token akses, detik. **Pendek disengaja.**
 *
 * ⚠️ Token panjang menyenangkan sampai satu bocor: tak ada cara menariknya kembali selain memutar
 * kunci seluruh tenant. Lima belas menit cukup untuk sesi kasir yang sibuk, dan cukup pendek
 * supaya kebocoran punya batas waktu.
 */
export const ACCESS_TOKEN_TTL_SECONDS = 900;
/**
 * ⭐⭐⭐ **Umur token PERANGKAT — 10 jam, satu shift penuh** (Imron, 2026-08-06).
 *
 * ⛔ 15 menit benar untuk klien OPERATOR: skrip meminta token, memakainya, selesai. Ia salah total
 * untuk terminal kasir — di sana token yang mati berarti **layar berhenti melayani tamu**, dan
 * satu-satunya yang bisa memperbaruinya (`device-agent`) tak ada di tablet.
 *
 * ⚠️ Yang membuat ini tetap aman bukan umurnya melainkan **pencabutannya**: perangkat yang
 * dinonaktifkan di portal ditolak `AUTH-403-DEVICE_UNKNOWN` pada permintaan berikutnya (`whoAmI`),
 * dan rahasia pasangannya berhenti bisa menukar token baru. Umur pendek yang dibayar dengan kasir
 * yang tak bisa jualan bukan keamanan — ia cuma memindahkan kerusakan ke tempat yang lebih mahal.
 *
 * ⛔ Sengaja TERPISAH dari `ACCESS_TOKEN_TTL_SECONDS`: klien platform (`platform:provision`) memang
 * tak butuh umur panjang, dan memanjangkannya sekalian berarti membayar risiko tanpa membeli apa pun.
 */
export const DEVICE_TOKEN_TTL_SECONDS = 10 * 60 * 60;
/**
 * ⭐ **Scope yang didapat perangkat saat dipasangkan.**
 *
 * ⛔ Tetap, bukan diminta klien: perangkat yang boleh memilih scope-nya sendiri adalah perangkat
 * yang bisa meminta lebih. Perbedaan antar-peran perangkat (till vs stasiun entri vs KDS) TIDAK
 * hidup di sini — ia ditegakkan `device.role` (K20/K21), yang tak bisa diubah dari tablet.
 */
export const DEVICE_SCOPES = Object.freeze(['pos:operate', 'master:read']);
/** Algoritma tanda tangan — **PS256**, sama dengan OSB Nexus. Jangan buat mekanisme kedua (§10b). */
export const TOKEN_ALG = 'PS256';
/* ────────────────────────────────────────────────────────────────────────────
 * Konsol portal — pintu MANUSIA
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * ⭐ Peran pengguna konsol. Sengaja **tiga**, bukan matriks izin bebas.
 *
 * Matriks izin per-pengguna terdengar fleksibel, lalu dipakai satu kali saat onboarding dan tak
 * pernah ditinjau lagi — dan yang tersisa adalah tenant yang semua orangnya "owner" karena itu
 * satu-satunya kombinasi yang pasti bekerja. Tiga peran bisa dijelaskan dalam satu kalimat, dan
 * yang bisa dijelaskan bisa ditinjau.
 */
/*
 * ⭐⭐ **`admin` — wakil pemilik** (Imron, 2026-08-07: *"role owner kalau menyerahkan pekerjaannya
 * ke orang kepercayaannya bagaimana? Dia harus bisa semuanya kecuali manipulasi owner."*).
 *
 * ⛔ Sengaja peran KEEMPAT, bukan `manager` yang dilonggarkan. `manager` sudah memegang
 * `master:write`; kalau ia juga boleh menerbitkan pengguna, tiap orang yang boleh mengubah harga
 * bisa memberi dirinya akses permanen. Yang dibutuhkan pemilik bukan manajer yang lebih longgar,
 * melainkan **wakil**: boleh semuanya, kecuali menyentuh sumber wewenangnya sendiri.
 */
export const PORTAL_ROLES = Object.freeze(['owner', 'admin', 'manager', 'viewer']);
/**
 * Scope yang dibawa sesi konsol per peran.
 *
 * ⛔ `viewer` tak pernah menulis; `manager` menulis master tapi tak menyentuh platform. Yang
 *   memisahkan keduanya bukan sopan santun — ia yang membuat "beri akses laporan ke akuntan"
 *   berhenti berarti "beri akses mengubah harga".
 */
export function scopesForPortalRole(role) {
    if (role === 'viewer')
        return ['master:read', 'reports:read'];
    /*
     * ⛔⛔⛔ **`platform:read` TAK PERNAH diterbitkan untuk sesi tenant** — dan sampai 2026-08-07 ia
     * diterbitkan untuk `owner`.
     *
     * `GET /platform/tenants` hanya menuntut scope itu, tanpa memeriksa bahwa tokennya token PLATFORM
     * (`tenantCode = null`). ⇒ pemilik tenant `arena` bisa menyebut SELURUH pelanggan di server ini.
     * Bukan data transaksi, tapi daftar nama pelanggan pesaing sudah cukup merusak — dan tak ada satu
     * pun galat yang muncul, karena secara teknis semuanya "sesuai scope".
     *
     * ⚠️ Ditutup di DUA tempat: di sini (tak diterbitkan) dan di `requirePlatformToken` (tak diterima
     * walau kebetulan ada). Satu lapis saja berarti scope yang bocor lewat jalur lain langsung
     * membuka pintunya lagi.
     *
     * ⭐ `owner` dan `admin` membawa scope yang SAMA — pembedanya bukan scope, melainkan **peran**,
     * dan itu ditegakkan di `console-user.service` (siapa boleh menyentuh siapa). Membedakannya lewat
     * scope berarti "boleh mengelola pengguna" jadi sesuatu yang bisa menempel di token M2M.
     */
    return ['master:read', 'master:write', 'reports:read', 'ops:read'];
}
/**
 * ⭐⭐ Scope untuk sesi konsol yang dibuka dengan **PIN di dalam outlet**.
 *
 * ⛔ **Tanpa `master:write` — dan itu inti pembelahan dua pintu** (Imron, 2026-08-02). PIN 6 digit
 * boleh membuka layar yang MELIHAT (laporan hari ini, kesehatan sinkron, daftar meja), tapi tak
 * boleh mengubah harga. Yang mengubah master harus lewat pintu berkata sandi.
 *
 * ⚠️ Konsekuensinya harus jujur di layar: tombol yang menulis TIDAK ditampilkan pada sesi PIN,
 * bukan ditampilkan lalu ditolak server.
 */
export const OUTLET_CONSOLE_SCOPES = Object.freeze([
    'master:read',
    'reports:read',
    'ops:read',
]);
//# sourceMappingURL=auth.js.map